import React, { Suspense, useEffect } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { logActivity } from "./helpers/activity-log";
import { enviarOrigenSiHaySesion } from "./helpers/origen-registro";
import { moduleFromPathname } from "./helpers/modulo-de-ruta";
import { installSwReloadGuard } from "./helpers/sw-reload-guard";
import { installVersionNueva } from "./helpers/version-nueva";
import { exencionConocida, sesionVencida } from "./helpers/idle-session";
import {
  cerrarPorInactividad,
  cerrarSalvoQueSeaExento,
} from "./helpers/idle-telemetria";
import { logout } from "./helpers/authentication-helpers";
import Authentication from "./views/Authentication";
import IdleLogoutGuard from "./components/IdleLogoutGuard";
import MicRemotoGate from "./components/MicRemotoGate";
import UsageLimitDialog from "./components/UsageLimitDialog";
import ProtectedRoute from "./components/routes/ProtectedRoute";
import RequireAuth from "./components/routes/RequireAuth";
import RequireSuperuser from "./components/routes/RequireSuperuser";
import ProfileCompletionGate from "./components/routes/ProfileCompletionGate";
import TosAcceptanceGate from "./components/routes/TosAcceptanceGate";
import SuscripcionGate from "./components/routes/SuscripcionGate";
import PausaInactividadGate from "./components/routes/PausaInactividadGate";
import ProfileRequiredGate from "./components/routes/ProfileRequiredGate";
import ReintroGate from "./components/routes/ReintroGate";
import AppCelularGate from "./components/pwa/AppCelularGate";
import RequiereOnboardingCompleto from "./components/routes/RequiereOnboardingCompleto";
import DxNavbar from "./components/navbars/DxNavbar";
import RetornoPago from "./components/checkout/RetornoPago";
// #1887 · jornada de 12 h con botón y jornadas sueltas: confirmación, avisos de corte de IA, compra y vuelta de Flow.
import JornadaConfirmarDialog from "./components/jornada/JornadaConfirmarDialog";
import JornadaAvisosGate from "./components/jornada/JornadaAvisosGate";
import CompraSueltaDialog from "./components/suelta/CompraSueltaDialog";
import RetornoSuelta from "./components/suelta/RetornoSuelta";
import Footer from "./components/Footer"; // Importa el Footer
// import KwDetectorTool from "./components/KwDetectorTool"; // apagado 01-oct-2026 (pedido de Sebastián): la pill flotaba sobre la vista
import CulturesAlertPopup from "./components/cultures/CulturesAlertPopup";
import InstallAppGuide from "./components/pwa/InstallAppGuide";
import VersionNuevaBar from "./components/VersionNuevaBar";
import BannerMantenimiento from "./components/BannerMantenimiento";
import AcidBaseAnalysisDialog from "./components/visualizer/acid_base/AcidBaseAnalysisDialog";
import { useAcidBase } from "./components/context/AcidBaseContext";
import { usePdfPreview } from "./components/context/PdfPreviewContext";
import Home from "./views/Home";
import ErrorBoundary from "./components/ErrorBoundary";
import PdfPreviewDialog from "./components/visualizer/pdf_preview/PdfPreviewDialog";
import LoadingScreen from "./components/LoadingScreen";
import { lazyConReintento } from "./helpers/lazy-con-reintento";


// #1717 R3: cada vista fuera de las de entrada (`/`, `/authentication`, `/turno`) va en su propio
// chunk. Antes las 64 vistas viajaban en un solo `main.js` de 7,1 MB, y el médico que solo usa el
// Turno descargaba también el panel de admin, el grafo, los perfiles y el transcriptor (medido
// 16-oct-2026; desde Venezuela eran varios segundos). `lazyConReintento` y no `React.lazy` a
// secas: un chunk que no llega reintenta una vez y, si fue un deploy, recarga con
// `version-nueva.js` en vez de dejar la vista en blanco (R6). Cubre R3 — .claude/specs/1717-velocidad/
const RawQuery = lazyConReintento(() => import("./views/RawQuery"));
const RecoverPassword = lazyConReintento(() => import("./views/RecoverPassword"));
const BajaComunicaciones = lazyConReintento(() => import("./views/publico/BajaComunicaciones"));
const CuentaEliminada = lazyConReintento(() => import("./views/publico/CuentaEliminada"));
const NewPassword = lazyConReintento(() => import("./views/NewPassword"));
const WaitingForAccess = lazyConReintento(() => import("./views/WaitingForAccess"));
const Record = lazyConReintento(() => import("./views/Record"));
const DxFinder = lazyConReintento(() => import("./views/DxFinder"));
const DxGraph = lazyConReintento(() => import("./views/DxGraph"));
const Findings = lazyConReintento(() => import("./views/admin/Findings"));
const Keywords = lazyConReintento(() => import("./views/admin/Keywords"));
const UsersAdmin = lazyConReintento(() => import("./views/admin/users/UsersAdmin"));
const RecordChangelog = lazyConReintento(() => import("./views/admin/RecordChangelog"));
const PendingReview = lazyConReintento(() => import("./views/PendingReview"));
const TermsAndConditions = lazyConReintento(() => import("./views/authentication/TermsAndConditions"));
const TerminosInternacionales = lazyConReintento(() => import("./views/authentication/TerminosInternacionales"));
const Transcriptor = lazyConReintento(() => import("./views/Transcriptor"));
const Informes = lazyConReintento(() => import("./views/Informes"));
const Complicaciones = lazyConReintento(() => import("./views/Complicaciones"));
const ExamsVisualizer = lazyConReintento(() => import("./views/ExamsVisualizer"));
const ProfileView = lazyConReintento(() => import("./views/profile/ProfileView"));
const ProfileEdit = lazyConReintento(() => import("./views/profile/ProfileEdit"));
const VerificationWizard = lazyConReintento(() => import("./views/profile/VerificationWizard"));
const CompletarDatos = lazyConReintento(() => import("./views/profile/CompletarDatos"));
const Bienvenida = lazyConReintento(() => import("./views/onboarding/Bienvenida"));
const PublicProfile = lazyConReintento(() => import("./views/directory/PublicProfile"));
const IndicacionesAmbulatorias = lazyConReintento(() => import("./views/IndicacionesAmbulatorias"));
const EpicrisisRadonc = lazyConReintento(() => import("./views/EpicrisisRadonc"));

// #1717 R12: el Turno (~1,2 MB con sus paneles) va en su propio chunk. Los leads entran por un
// link directo a /authentication y ahí se registran: antes esperaban también todo el Turno, que
// todavía no pueden usar. Con sesión iniciada el chunk empieza a bajar al arrancar, en paralelo
// con el main y sin esperar a que se navegue, así el médico que vuelve no espera más que antes
// (webpack reusa esa misma descarga cuando la ruta la pide). Cubre R12 — .claude/specs/1717-velocidad/
const importarTurno = () => import("./views/Turno");
const Turno = lazyConReintento(importarTurno);
let turnoPrecargado = false;
function precargarTurnoSiHaySesion() {
  if (turnoPrecargado) return;
  try {
    // #1774 perf: el panel de administración de cuentas no usa el Turno (~3 MB con la planilla y el generador de documentos): ahí
    // la precarga solo le quitaba ancho de banda a los pedidos de la tabla. Al salir de /admin/users la navegación la dispara.
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/admin/users")) return;
    if (typeof window !== "undefined" && window.localStorage.getItem("authToken")) {
      turnoPrecargado = true;
      importarTurno().catch(() => {
        turnoPrecargado = false; // sin red: se reintenta en la próxima navegación
      });
    }
  } catch (_) {
    /* sin localStorage: se baja al entrar a /turno */
  }
}
precargarTurnoSiHaySesion();

// #1301: la Comunidad (globo de colegas) va en un chunk aparte. d3-geo, topojson-client y el
// mapa de países pesan lo que ninguna otra vista: no deben engordar `main.*.js` de quien
// nunca abre la vista. Cubre R13 — .claude/specs/1301-comunidad-globo/
const Comunidad = lazyConReintento(() => import("./views/comunidad/Comunidad"));

// Popup ácido-base montado a nivel App (fuera del Dialog del visualizador) para
// que quede POR ENCIMA del viewer — un Dialog anidado dentro del viewer quedaba
// atrás en el build de prod pese al z-index. Mismo patrón que CulturesAlertPopup.
function GlobalAcidBaseDialog() {
  const { open, groups, closeAcidBase } = useAcidBase();
  return (
    <AcidBaseAnalysisDialog open={open} onClose={closeAcidBase} groups={groups} />
  );
}

// Popup de vista previa de PDF de examen — montado a nivel App (fuera del viewer)
// por el mismo motivo de stacking que el de ácido-base. Ver PdfPreviewContext.
// #1717 R4: el diálogo se carga con la app (es liviano) y adentro difiere solo pdf.js, con su propio
// límite de error: si pdf.js no baja, el diálogo muestra su error de siempre (F5 del revisor v2).
function GlobalPdfPreviewDialog() {
  const { open, pdf, closePdfPreview } = usePdfPreview();
  return <PdfPreviewDialog open={open} pdf={pdf} onClose={closePdfPreview} />;
}

function AppShell() {
  // La auth es full-screen one-view: navbar y footer (ambos position:fixed)
  // quedan montados como overlay encima del login, sin reservar espacio (sin
  // padding inferior → sin franja negra ni scroll).
  const location = useLocation();
  const navigationType = useNavigationType();
  // Las tres pantallas de acceso son `position:fixed; inset:0`, así que el
  // padding que el layout reserva para el footer les deja una franja muerta
  // abajo.
  const AUTH_PATHS = ["/authentication", "/recuperar-clave", "/nueva-clave"];
  const isAuth = AUTH_PATHS.includes(location.pathname);
  // El cockpit del turno se dimensiona contra la ventana: acá se apaga el
  // minHeight/padding globales para que el documento no quede scrolleable.
  const isTurnoView = location.pathname.startsWith("/turno");
  // #1301: la Comunidad también es una vista a pantalla completa (el globo se mide contra la
  // ventana): sin minHeight, sin padding de footer y sin Footer, igual que el Turno.
  const isComunidadView = location.pathname.startsWith("/comunidad");
  const isFullView = isTurnoView || isComunidadView;

  // Al cambiar de ruta, el scroll vuelve arriba.
  //
  // Un cambio de ruta en una SPA no reposiciona nada: React Router cambia el
  // árbol y `window.scrollY` se queda donde estaba. Eso rompía la navegación
  // entera de forma invisible, porque el navbar es `position:absolute; top:12px`
  // (vive en el tope del DOCUMENTO, no de la ventana) y además se desvanece
  // solo cuando `scrollY >= 20`. Resultado medido el 09-sep-2026: apretar
  // "Completar después" abajo del wizard de verificación —que es donde está el
  // botón— aterrizaba en el Home con scrollY=104, o sea con el navbar 104 px
  // arriba del borde y con opacity 0. La app quedaba sin barra y sin usuario
  // hasta recargar a mano, que es exactamente lo que reportó Sebastián.
  //
  // El bug no era del wizard: le pasaba a CUALQUIER navegación hecha desde una
  // página scrolleada. (El cockpit del Turno ya se lo había parchado solo, con
  // su propio `window.scrollTo(0, 0)` al montar.)
  //
  // POP —el botón atrás del navegador— se respeta: ahí el navegador restaura la
  // posición anterior y pisarla sería perder el lugar donde el médico estaba.
  useEffect(() => {
    if (navigationType === "POP") return;
    window.scrollTo(0, 0);
  }, [location.pathname, navigationType]);

  // #1731: el origen del anuncio (si lo hay) se manda apenas existe sesión: tras registrarse o
  // ingresar la app navega, y este efecto lo toma. Una sola vez (lo marca origen-registro.js).
  useEffect(() => {
    enviarOrigenSiHaySesion();
  }, [location.pathname]);

  // Telemetría: emite `module_open` en cada cambio de ruta principal. meta.module
  // = nombre de la vista (enum), NUNCA la URL con params. Fire-and-forget:
  // logActivity nunca lanza.
  // #1717 (O3 del revisor v1): el lead que se registra en esta misma carga no tenía sesión al
  // arrancar; apenas la tiene (la navegación que sigue al ingreso) empieza a bajar el Turno.
  useEffect(() => {
    precargarTurnoSiHaySesion();
  }, [location.pathname]);

  useEffect(() => {
    const module = moduleFromPathname(location.pathname);
    logActivity("module_open", { module, meta: { module } });
  }, [location.pathname]);

  return (
    <>
      <DxNavbar />
      {/* Gate de Términos de Uso: PRIMERO en el onboarding. Fuerza a aceptar los
          términos (click-through) antes de operar. Renderea un overlay bloqueante
          por encima de todo, así que precede al ProfileCompletionGate: aceptar el
          marco de datos (Ley 21.719) es condición previa a subir docs de perfil.
          Ver components/routes/TosAcceptanceGate.js. */}
      {/* Tope diario de uso alcanzado. Va acá y no en cada vista porque el 429
          puede venir de cualquier módulo que genere con LLM o dicte por voz
          (Turno, Informes, Transcriptor). Se abre solo escuchando el evento
          `smartdx:usage-limit` que emite el interceptor de get-client. */}
      <UsageLimitDialog />
      <TosAcceptanceGate />
      {/* Bloqueo por pago vencido o prueba vencida (#862, R30, R32), y el
          aviso no bloqueante mientras la cuenta está en gracia (R31).
          INMEDIATAMENTE DESPUÉS del gate de Términos: aceptar el marco de
          datos es previo a cualquier cosa, pero si la cuenta ya venció, no
          tiene sentido dejarla seguir al wizard de perfil. Ver
          components/routes/SuscripcionGate.js. */}
      {/* #1599 R33: la pantalla de lo que falta tampoco lleva la tarjeta de prueba encima (tapaba Guardar a 390). */}
      <SuscripcionGate
        enOnboarding={
          location.pathname.startsWith("/perfil/verificacion") || location.pathname.startsWith("/perfil/completar")
        }
      />
      {/* Pausa por inactividad de las cuentas gratis (#1583, R25-R28): modal
          centrado del mockup A (pausada, revisando, aviso de reactivada).
          Va justo después de SuscripcionGate: con Términos pendientes o con otro
          corte (demo o pago vencido) se esconde sola, nunca dos capas (R26). Ver
          components/routes/PausaInactividadGate.js. */}
      <PausaInactividadGate />
      {/* Vuelta de Flow (`/perfil?pago=flow`): resultado del pago con sondeo (#1680, R31). */}
      <RetornoPago />
      {/* #1887 (R36, R74): vuelta de Flow tras comprar jornadas sueltas (`/perfil?pago=suelta`); `?pago=flow` sigue siendo del de arriba. */}
      <RetornoSuelta />
      {/* #1887: la confirmación de la jornada (también la abre un 403 `requiere_boton`), la compra de sueltas y los avisos de corte de IA
          (`sin_jornadas` -> `jornadas-agotadas`). Ninguno tapa la app: leer, copiar, editar y guardar siguen siempre (R52). */}
      <JornadaConfirmarDialog />
      <CompraSueltaDialog />
      <JornadaAvisosGate />
      {/* #1529: ya no hay gate de documentos. El carnet y el certificado de la
          Superintendencia son un recordatorio dentro del Turno (chip de
          `AvisosPrimerosPasos`), nunca un overlay ni un 403. */}
      {/* Gate de onboarding: fuerza a un usuario nuevo (sin has_access y con
          verificación NOT_SUBMITTED) a completar su info básica + Superintendencia
          en el VerificationWizard antes de operar. No bloquea admins ni cuentas
          demo/habilitadas. Ver components/routes/ProfileCompletionGate.js. */}
      <ProfileCompletionGate />
      {/* Datos faltantes al iniciar sesión (#1416, generaliza el
          `PhoneRequiredGate` de #1401): overlay que pide SOLO lo que falte de
          celular/etapa/institución (`/me` → `missing_profile_fields`). El
          celular nunca se salta (R43); etapa e institución admiten "Después"
          por sesión de login (R45/R46). Se esconde mientras el gate de
          Términos o el de Suscripción estén activos (R40). Ver
          components/routes/ProfileRequiredGate.js. */}
      <ProfileRequiredGate />
      {/* #1599 R8: desde este spec `missing_profile_fields` trae solo el celular; etapa e
          institución se piden obligatorias en "Quién eres" y en /perfil/completar. */}
      {/* Rescate de inactivos (spec rescate-inactivos, R9): cuenta con
          `reintro_pendiente` → una vez por sesión al onboarding. Va después de
          los gates porque espera a que ninguno dibuje un overlay. Ver
          components/routes/ReintroGate.js. */}
      <ReintroGate />
      {/* #1686: pop-up "Lleva SmartDx a tu celular" (B1) para quien ya usa bien la app en el
          computador, solo en /, /home y /turno. Espera a que ningún gate de alta dibuje un overlay y no
          sale en la carga en que hubo rescate de inactivos, un tutorial, el de camas pendiente u otro
          diálogo en el DOM. En /turno además espera la señal de Turno.js (helpers/turno-arranque.js):
          sin ella, o con un selector o formulario de unidad abierto, no sale en esa carga. Detalle y
          límites en components/pwa/AppCelularGate.jsx. */}
      <AppCelularGate />
      {/* Contenedor principal con padding-bottom para evitar superposición.
          Excepción /turno: el cockpit es una vista de alto fijo que se mide
          contra la ventana. Con minHeight:100vh + paddingBottom aquí, el
          documento medía siempre ~164px más que el viewport (navbar + padding +
          footer), así que la página quedaba scrolleable por debajo del cockpit:
          cualquier scroll (incluido el automático al enfocar un textarea)
          movía las 3 cards y su borde inferior aparecía en un lugar distinto
          cada vez. En /turno el alto lo manda el cockpit. */}
      <div
        style={{
          minHeight: isFullView ? 0 : "100vh", // Asegura que el contenido ocupe toda la altura
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            flex: 1, // Hace que este contenedor crezca y ocupe todo el espacio disponible
            paddingBottom: isAuth || isFullView ? 0 : "60px", // Altura del Footer
            // Banner de mantenimiento (#1757 v2): mientras está a la vista empuja el contenido en vez de
            // taparlo (el mockup C lo pone en flujo). Vale 0px sin banner. Las pantallas de acceso son
            // `fixed` y no se enteran: ellas bajan su columna con `--acceso-pt-mantenimiento`.
            paddingTop: "var(--banner-mantenimiento-h, 0px)",
          }}
        >
          {/* #1717 R3/R6: mientras llega el chunk de una vista se ve la misma pantalla de carga de los
              guards de ruta. El ErrorBoundary se limpia al cambiar de ruta: si un chunk no se pudo
              bajar, el médico ve "Recargar" en esa vista y puede seguir navegando. */}
          <ErrorBoundary resetKey={location.pathname}>
          <Suspense
            fallback={
              <div data-testid="perf-1717-suspense">
                <LoadingScreen fullscreen={false} />
              </div>
            }
          >
          <Routes>
            {/* #1599: `/`, `/home` y `/turno` son las rutas de aterrizaje. Con preguntas de
                Quién eres sin responder (`onboarding_faltante`), `RequiereOnboardingCompleto`
                muestra la pantalla de lo que falta EN VEZ de la ruta: el Turno ni se monta. */}
            <Route
              path="/"
              element={
                <RequiereOnboardingCompleto>
                  <Home />
                </RequiereOnboardingCompleto>
              }
            />
            <Route path="/authentication" element={<Authentication />} />
            <Route path="/waiting-for-access" element={<WaitingForAccess />} />
          {/* Recuperación de clave: públicas, sin wrapper. /nueva-clave se abre
              desde el enlace del correo, casi siempre en el teléfono y sin
              sesión. */}
          <Route path="/recuperar-clave" element={<RecoverPassword />} />
          <Route path="/nueva-clave" element={<NewPassword />} />
          {/* #1683: baja de correos por link firmado (sin sesión) y pantalla final de eliminar cuenta. */}
          <Route path="/baja" element={<BajaComunicaciones />} />
          <Route path="/cuenta-eliminada" element={<CuentaEliminada />} />
            <Route
              path="/terminos-internacionales"
              element={<TerminosInternacionales />}
            />
            <Route
              path="/terms-and-conditions"
              element={<TermsAndConditions />}
            />
            <Route element={<RequireAuth />}>
              {/* /bienvenida ya no es una pantalla: la bienvenida y las 4
                  preguntas son los pasos 0 y 1 del wizard. La ruta se conserva
                  redirigiendo porque hay correos y enlaces guardados que
                  apuntan ahí. */}
              <Route path="/bienvenida" element={<Bienvenida />} />
              <Route path="/perfil/verificacion" element={<VerificationWizard />} />
              {/* #1599 R23: solo lo que falta de "Quién eres" (cuentas que se registraron sin responder). */}
              <Route path="/perfil/completar" element={<CompletarDatos />} />
              <Route path="/perfil/editar" element={<ProfileEdit />} />
              <Route path="/perfil" element={<ProfileView />} />
              {/* #1301: "Colegas" pasó a "Comunidad". /directorio se conserva redirigiendo
                  (hay enlaces y favoritos guardados; el menú y Ajustes ya apuntan a /comunidad). */}
              <Route path="/directorio" element={<Navigate to="/comunidad" replace />} />
              <Route
                path="/comunidad"
                element={
                  <Suspense fallback={null}>
                    <Comunidad />
                  </Suspense>
                }
              />
              <Route path="/u/:userId" element={<PublicProfile />} />
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route
                path="/home"
                element={
                  <RequiereOnboardingCompleto>
                    <Home />
                  </RequiereOnboardingCompleto>
                }
              />
              <Route path="/record/:recordId" element={<Record />} />
              <Route path="/graph/:recordId" element={<DxGraph />} />
              <Route path="/admin/findings" element={<Findings />} />
              <Route path="/admin/keywords" element={<Keywords />} />
            </Route>
            <Route element={<RequireSuperuser />}>
              <Route path="/admin/users" element={<UsersAdmin />} />
              <Route path="/admin/changelog" element={<RecordChangelog />} />
              <Route path="/admin/pendientes" element={<PendingReview />} />
            </Route>
            <Route element={<ProtectedRoute requiredFlag="can_access_raw_query" />}>
              <Route path="/raw-query" element={<RawQuery />} />
            </Route>
            <Route element={<ProtectedRoute requiredFlag="can_access_complicaciones" />}>
              <Route path="/complicaciones" element={<Complicaciones />} />
            </Route>
            <Route element={<ProtectedRoute requiredFlag="can_access_transcriptor" />}>
              <Route path="/transcriptor" element={<Transcriptor />} />
            </Route>
            <Route element={<ProtectedRoute requiredFlag="can_access_turno" />}>
              <Route
                path="/turno"
                element={
                  <RequiereOnboardingCompleto>
                    <Turno />
                  </RequiereOnboardingCompleto>
                }
              />
            </Route>
            <Route
              element={<ProtectedRoute requiredFlag="can_access_ingreso_epicrisis" />}
            >
              <Route path="/informes" element={<Informes />} />
            </Route>
            <Route element={<ProtectedRoute requiredFlag="can_access_finder" />}>
              <Route path="/finder" element={<DxFinder />} />
            </Route>
            <Route
              element={<ProtectedRoute requiredFlag="can_access_indicaciones_ambulatorias" />}
            >
              <Route
                path="/recetas-ambulatorias"
                element={<IndicacionesAmbulatorias />}
              />
              {/* Deep link al tab "Histórico" embebido — sigue al mismo
                  componente que detecta la URL y abre el tab correspondiente.
                  El wrapper standalone HistorialRecetas queda como compat
                  (importable en otros lados si fuera necesario). */}
              <Route
                path="/recetas-ambulatorias/historial"
                element={<IndicacionesAmbulatorias />}
              />
            </Route>
            <Route
              element={<ProtectedRoute requiredFlag="can_access_epicrisis_radonc" />}
            >
              <Route path="/epicrisis-radonc" element={<EpicrisisRadonc />} />
            </Route>
            <Route
              element={
                <ProtectedRoute requiredFlag="can_access_visualizador_examenes" />
              }
            >
              <Route
                path="/visualizador-examenes"
                element={
                  <ErrorBoundary>
                    <ExamsVisualizer />
                  </ErrorBoundary>
                }
              />
            </Route>
            <Route path="*" element={<p>There's nothing here: 404!</p>} />
          </Routes>
          </Suspense>
          </ErrorBoundary>
        </div>
        {/* Fix #1255 (S2, regresión del propio fix del dock): `.app-footer`
            es `position:fixed; bottom:10px; right:14px` y se montaba en
            TODAS las rutas, incluida /turno. Mientras el dock estaba
            recortado (S1) nunca se cruzaban; al arreglar S1 el dock quedó
            pintado entero justo en esa franja y el footer social le tapaba
            el aviso "Sin guardar" (61%), volvía el chevron un afordance
            muerto, y un click en la barra abría Instagram en vez del bloc.
            /turno ya decide no reservarle espacio (`paddingBottom` arriba),
            así que la coherencia es no montarlo ahí tampoco — mismo
            `isFullView` (Turno y Comunidad, #1301) que ya gobierna esa decisión. */}
        {!isFullView && <Footer />} {/* Footer fijo al fondo */}
      </div>
      {/* <KwDetectorTool /> apagado 01-oct-2026: reconocedor de KW (superuser + web). Para reactivarlo, descomentar esto y el import. */}
      <CulturesAlertPopup /> {/* Popup global: cultivos nuevos detectados al descargar exámenes */}
      <GlobalAcidBaseDialog /> {/* Popup global ácido-base — montado fuera del viewer para quedar encima */}
      <IdleLogoutGuard /> {/* Cierre por inactividad, pensado solo para computadores */}
      {/* El computador pide dictar: el aviso vive acá y no en el Turno para que
          aparezca en CUALQUIER parte de la app. Alcanza con tenerla abierta. */}
      <MicRemotoGate />
      <GlobalPdfPreviewDialog /> {/* Popup global vista previa PDF de examen — montado fuera del viewer para quedar encima */}
      <InstallAppGuide /> {/* Modo celular: cómo dejar SmartDx como app en el teléfono (una vez por aparato) */}
      <VersionNuevaBar /> {/* Aviso no bloqueante de versión nueva tras un deploy (#1451) */}
      {/* Banner global «Estamos actualizando SmartDx» ante un 502/503 de deploy (#1757 v2, opción C): fuera de
          AlcanceOscuro (sigue el tema), no bloquea ni toma foco. Estado en helpers/mantenimiento.js. */}
      <BannerMantenimiento />
    </>
  );
}

// Escucha el aviso del service worker "suicida" antes de que recargue la
// pestaña, para alcanzar a guardar lo que no salió a la red (ver
// helpers/sw-reload-guard.js · Planner #980). A nivel de módulo: una sola vez
// por carga, sin depender de que un componente monte.
installSwReloadGuard();

// Detector de versión nueva (#1451): la pestaña abierta antes de un deploy se
// entera sola y se actualiza sin que nadie tenga que "borrar el caché" (ver
// helpers/version-nueva.js). Solo en el build: en dev y en jest no hay
// `main.<hash>.js` que comparar, y ahí un error de chunk del HMR no debe recargar.
if (process.env.NODE_ENV === "production") {
  installVersionNueva();
}

// El cierre por inactividad vive en un setInterval, o sea que solo corre con la
// pestaña abierta. El médico que apaga el computador del hospital sin cerrar
// sesión se le escapaba: al otro día alguien lo prendía y entraba a su ficha.
//
// Acá, a nivel de módulo, ANTES del primer render y de cualquier request con
// PHI, se compara la marca guardada contra el mismo umbral de 30 min. Si venció,
// al login. Para los usuarios exentos no aplica, ni donde `esEscritorio()` no ve
// un computador (pensado para el celular; sus trampas, en `idle-session.js`).
//
// En las rutas públicas de autenticación no se REDIRIGE, pero la sesión vencida
// igual muere. Son dos cosas distintas y confundirlas costó un bug: al sacar el
// chequeo de esas rutas, una sesión ajena vencida hace horas revivía sola —
// alcanzaba con que el siguiente médico abriera la pestaña que quedó en
// /recuperar-clave, porque el guard le renovaba el reloj. Borrar sin redirigir
// conserva el `?token=` del enlace del correo (que es lo que había que proteger)
// y deja la sesión muerta igual.
if (sesionVencida({ ignorarExencion: true })) {
  if (exencionConocida()) {
    // Ya se sabe quién es: se decide en el acto, sin depender de la red.
    if (!sesionVencida()) {
      /* es exento: la sesión se queda */
    } else {
      cerrarPorInactividad("arranque");
    }
  } else {
    // No se sabe todavía. Se le da al backend unos segundos para decirlo, pero
    // con techo: si no contesta, la sesión vencida igual muere.
    cerrarSalvoQueSeaExento("arranque");
  }
}

function App() {
  return (
    <Router>
      <AppShell />
    </Router>
  );
}

export default App;
